import React from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownContentProps {
  content?: string | null;
  className?: string;
}

const linkShouldOpenNewTab = (href?: string) => {
  if (!href) return false;
  return /^(https?:)?\/\//i.test(href);
};

const markdownComponents: Components = {
  h1: ({ node, ...props }) => (
    <h1 className="text-3xl md:text-4xl font-light mb-6 mt-10 first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-2xl md:text-3xl font-light mb-4 mt-8" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-xl font-medium mb-3 mt-6" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="text-lg font-medium mb-3 mt-5" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="text-base leading-relaxed text-foreground/80 mb-4" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 space-y-2 mb-5 text-foreground/80" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 space-y-2 mb-5 text-foreground/80" {...props} />
  ),
  li: ({ node, ...props }) => (
    <li className="leading-relaxed" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-divider pl-4 italic text-foreground/70 bg-content2/40 py-2 mb-5" {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-foreground" {...props} />
  ),
  em: ({ node, ...props }) => (
    <em className="italic text-foreground/80" {...props} />
  ),
  a: ({ node, href, ...props }) => (
    <a
      href={href}
      target={linkShouldOpenNewTab(href) ? "_blank" : undefined}
      rel={linkShouldOpenNewTab(href) ? "noopener noreferrer" : undefined}
      className="text-primary hover:underline break-words"
      {...props}
    />
  ),
  img: ({ node, src, alt, ...props }) => (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-auto rounded-lg my-6"
      {...props}
    />
  ),
  code: (markdownProps) => {
    const { inline, className, children, ...rest } = markdownProps as any;
    if (inline) {
      return (
        <code className="px-1.5 py-0.5 rounded bg-content2/70 text-sm" {...(rest as React.HTMLAttributes<HTMLElement>)}>
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-content2/60 rounded-lg p-4 overflow-auto text-sm">
        <code className={className} {...(rest as React.HTMLAttributes<HTMLElement>)}>
          {children}
        </code>
      </pre>
    );
  }
};

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className }) => {
  if (!content || !content.trim()) return null;

  const combinedClassName = ["space-y-4", className].filter(Boolean).join(" ");

  return (
    <div className={combinedClassName}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
